/**
 * ZKAuthModal Component
 *
 * Main modal for ZK biometric authentication.
 * Supports both enrollment and login flows with tab switching.
 */

import React, { useState, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';

import { FaceEnrollment } from './FaceEnrollment';
import { FaceVerification } from './FaceVerification';
import type { ZKAuthModalProps, ZKCredential } from '../types';

// ============================================================================
// Component
// ============================================================================

export const ZKAuthModal: React.FC<ZKAuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode = 'both',
}) => {
  // State
  const [activeTab, setActiveTab] = useState(mode === 'enroll' ? 0 : 1);
  const [isProcessing, setIsProcessing] = useState(false);

  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('gray.50', 'gray.700');

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleEnrollmentSuccess = useCallback(
    (credential: ZKCredential) => {
      console.log('[ZK Auth] Enrollment complete, switching to login');
      // After enrollment, switch to login tab
      setActiveTab(1);
    },
    []
  );

  const handleAuthSuccess = useCallback(
    (token: string) => {
      console.log('[ZK Auth] Authentication successful');
      if (onSuccess) {
        onSuccess(token);
      }
      onClose();
    },
    [onSuccess, onClose]
  );

  const handleAuthError = useCallback((error: string) => {
    console.warn('[ZK Auth] Authentication error:', error);
    // Error is already displayed in the component
  }, []);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      isCentered
      closeOnOverlayClick={!isProcessing}
      closeOnEsc={!isProcessing}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent bg={bgColor} borderRadius="xl" overflow="hidden">
        {/* Header */}
        <ModalHeader bg={headerBg} py={3}>
          <HStack spacing={2}>
            <Text fontSize="xl">🔐</Text>
            <Text fontWeight="bold">Face Authentication</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={isProcessing} />

        <ModalBody p={0}>
          {/* Tabs */}
          {mode === 'both' ? (
            <Tabs
              index={activeTab}
              onChange={setActiveTab}
              variant="enclosed"
              colorScheme="blue"
            >
              <TabList px={4} pt={2}>
                <Tab
                  _selected={{
                    color: 'blue.500',
                    borderColor: 'blue.500',
                    borderBottomColor: bgColor,
                  }}
                >
                  Login
                </Tab>
                <Tab
                  _selected={{
                    color: 'blue.500',
                    borderColor: 'blue.500',
                    borderBottomColor: bgColor,
                  }}
                >
                  Enroll
                </Tab>
              </TabList>

              <TabPanels>
                {/* Login Tab */}
                <TabPanel p={0}>
                  <FaceVerification
                    onSuccess={handleAuthSuccess}
                    onError={handleAuthError}
                    onCancel={handleCancel}
                  />
                </TabPanel>

                {/* Enroll Tab */}
                <TabPanel p={0}>
                  <FaceEnrollment
                    onSuccess={handleEnrollmentSuccess}
                    onCancel={handleCancel}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : mode === 'enroll' ? (
            <FaceEnrollment
              onSuccess={handleEnrollmentSuccess}
              onCancel={handleCancel}
            />
          ) : (
            <FaceVerification
              onSuccess={handleAuthSuccess}
              onError={handleAuthError}
              onCancel={handleCancel}
            />
          )}

          {/* Footer Info */}
          <Box px={4} py={3} borderTopWidth="1px">
            <VStack spacing={1}>
              <Text fontSize="xs" color="gray.500" textAlign="center">
                Your biometric data is processed locally and never sent to our servers.
              </Text>
              <HStack spacing={1} justify="center">
                <Text fontSize="xs" color="green.500">🔒</Text>
                <Text fontSize="xs" color="green.500" fontWeight="medium">
                  Zero-Knowledge Authentication
                </Text>
              </HStack>
            </VStack>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ZKAuthModal;
